import { type Action, type ThunkDispatch } from '@reduxjs/toolkit';

import {
    type ApiResult,
    type EidCategories,
    type EidCategory,
    type EidCategoryDataHash,
    type EmojiId,
    Emojis,
    NFTClient,
    type TagResponse,
    type VisualizerAllData,
    type VisualizerDataKeys,
} from '@app/client';
import { apiFactory } from '@app/client/apiFactory/api';
import config from '@app/config';
import { prefetch2FA } from '@app/helpers';

import { type RootState } from '../reducers';
import {
    ACTION_SET_EMOJI_ADMIN_WASM,
    ACTION_SET_EMOJI_TYPES_WASM,
    ACTION_SET_EMOJI_WASM,
    type EmojiActionTypes,
    type EmojiAdminWasm,
    type EmojiTypesWasm,
    type EmojiWasm,
    WasmType,
} from '../types/emoji';

import { setError } from './error';
import { getYatPageDetails } from './yat-page';

const loadingWasm = {};

export const setEmojiWasm = (wasm: EmojiWasm): EmojiActionTypes => ({
    type: ACTION_SET_EMOJI_WASM,
    wasm,
});

export const setEmojiAdminWasm = (wasm: EmojiAdminWasm): EmojiActionTypes => ({
    type: ACTION_SET_EMOJI_ADMIN_WASM,
    wasm,
});

export const setEmojiTypesWasm = (wasm: EmojiTypesWasm): EmojiActionTypes => ({
    type: ACTION_SET_EMOJI_TYPES_WASM,
    wasm,
});

export function loadWasms(type: WasmType) {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>, getState: () => RootState): Promise<void> {
        if (
            !loadingWasm[WasmType.EmojiWasm] &&
            !getState().emoji.wasm &&
            (type === WasmType.All || type === WasmType.EmojiWasm)
        ) {
            loadingWasm[WasmType.EmojiWasm] = true;
            await loadEmojiWasm(dispatch);
        }
        if (
            !loadingWasm[WasmType.EmojiAdminWasm] &&
            !getState().emoji.adminWasm &&
            (type === WasmType.All || type === WasmType.EmojiAdminWasm)
        ) {
            loadingWasm[WasmType.EmojiAdminWasm] = true;
            await loadAdminEmojiWasm(dispatch);
        }
        if (
            !loadingWasm[WasmType.EmojiTypesWasm] &&
            !getState().emoji.types &&
            (type === WasmType.All || type === WasmType.EmojiTypesWasm)
        ) {
            loadingWasm[WasmType.EmojiTypesWasm] = true;
            await loadEmojiTypesWasm(dispatch);
        }
    };
}

export async function loadEmojiWasm(dispatch: ThunkDispatch<RootState, void, Action>): Promise<void> {
    try {
        const emojiWasm = await import('@tari-labs/emojis-wasm-default');
        if (config.nodeEnv === 'development') {
            emojiWasm.init_debug();
        }
        dispatch(setEmojiWasm(emojiWasm));
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
        dispatch(
            setError({ error: `Unexpected error in load emojiWasm. [Message: ${err.message ?? err}]`, log: true }),
        );
    }
}

export async function loadAdminEmojiWasm(dispatch: ThunkDispatch<RootState, void, Action>): Promise<void> {
    try {
        const emojiAdminWasm = await import('@tari-labs/emojis-wasm-admin');
        if (config.nodeEnv === 'development') {
            emojiAdminWasm.init_debug();
        }
        dispatch(setEmojiAdminWasm(emojiAdminWasm));
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
        dispatch(setError({ error: `Unexpected error in load emojiAdminWasm. [Message: ${err.message}]`, log: true }));
    }
}

export async function loadEmojiTypesWasm(dispatch: ThunkDispatch<RootState, void, Action>): Promise<void> {
    try {
        const emojiTypesWasm = await import('@tari-labs/emojis-wasm-types');
        dispatch(setEmojiTypesWasm(emojiTypesWasm));
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
        dispatch(setError({ error: `Unexpected error in load emojiTypesWasm. [Message: ${err.message}]`, log: true }));
    }
}

/**
 * adminEmojiTags('🖖', '515');
 * @param eid
 * @param tagsArray
 */

export async function getEmojiTags(eid: string, tagsArray?: EidCategory[]): Promise<ApiResult<TagResponse>> {
    const tags = tagsArray ? tagsArray.join(',') : undefined;
    return apiFactory()
        .call(Emojis.EmojiTags.getCollection, {
            eid,
            query: {
                tags,
            },
        })
        .then((r) => {
            return r;
        })
        .catch((e) => {
            setError({
                ...e,
                log: true,
                source: 'Error getting admin emojis',
            })();
            return e;
        });
}

export function updateEmojiTag(eid: string, insert: EidCategories[], deleteTags?: EidCategoryDataHash[]) {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>): Promise<ApiResult<EmojiId[]>> {
        return apiFactory()
            .call(Emojis.EmojiTags.update, {
                eid,
                value: {
                    insert,
                    delete: deleteTags || [],
                },
            })
            .then((r) => {
                if (r.ok && r.value) {
                    dispatch(getYatPageDetails(r.value));
                }
                return r;
            })
            .catch((e) => {
                setError({
                    ...e,
                    log: true,
                    source: 'Error updating admin emojis tags',
                })();
                dispatch(getYatPageDetails(eid));
                return e;
            });
    };
}

export function deleteEmojiTag(eid: string, del: EidCategoryDataHash[]) {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>): Promise<ApiResult<EmojiId[]>> {
        return apiFactory()
            .call(Emojis.EmojiTags.remove, {
                prefetch: prefetch2FA(dispatch),
                eid,
                value: {
                    delete: del,
                },
            })
            .then((r) => {
                return r;
            })
            .catch((e) => {
                setError({
                    ...e,
                    log: true,
                    source: 'Error deleting admin emoji tags',
                })();
                return e;
            });
    };
}

export async function getAllVisualizerData(
    eid: EmojiId,
    keys?: VisualizerDataKeys[],
): Promise<ApiResult<VisualizerAllData>> {
    return apiFactory()
        .call(NFTClient.NFT.getVisualiserData, { eid, keys })
        .then((r) => {
            return r;
        })
        .catch((e) => {
            setError({
                ...e,
                log: true,
                source: 'Error getting visualizer data',
            })();
            return e;
        });
}
